import { createFileRoute } from "@tanstack/react-router";
import {
  IndustryMarketingPage,
  industryMarketingHead,
  type IndustryFAQ,
} from "@/components/IndustryMarketingPage";

const FAQS: IndustryFAQ[] = [
  {
    q: "How do I get more new chiropractic patients in Colorado Springs?",
    a: "The fastest path is a tuned Google Business Profile plus local SEO so you rank in the Map Pack for 'chiropractor near me' and 'chiropractor Colorado Springs', backed by a website that converts mobile bookings. We layer Google Ads on top to capture acute back-and-neck searches that won't wait for organic to catch up.",
  },
  {
    q: "What's the best marketing channel for a chiropractic clinic in Colorado?",
    a: "For most Colorado chiropractors the highest-ROI channel is local SEO + Google reviews — high-intent, low cost-per-patient, and recurring. Ads work well for new-patient specials and auto-accident searches. GEO AI is becoming critical because patients increasingly ask ChatGPT for a recommendation before they Google.",
  },
  {
    q: "How long does chiropractic SEO take to work?",
    a: "Most Colorado clinics see meaningful Map Pack movement in 60–120 days and stronger organic traffic within 4–6 months. Springs and Denver are competitive, so consistent monthly review velocity and on-page work compound much faster than one-off projects.",
  },
  {
    q: "Should I market my auto-accident and personal-injury cases separately?",
    a: "Yes. PI and auto-accident chiropractic is a totally different searcher (often urgent, often referred by an attorney) with much higher case value. We build a dedicated PI landing page, attorney referral content, and Google Ads tuned to that exact searcher.",
  },
  {
    q: "How do I rank in the Google Map Pack for chiropractors in Colorado?",
    a: "Three things matter most: a fully optimized Google Business Profile (categories, services, photos, posts), a steady stream of real Google reviews after every visit, and proximity-relevance signals from on-page SEO and local citations. We handle all three as part of our local SEO service.",
  },
];

export const Route = createFileRoute("/chiropractic-marketing-colorado")({
  head: () =>
    industryMarketingHead("Chiropractic", "chiropractic", "chiropractic-marketing-colorado", FAQS),
  component: () => (
    <IndustryMarketingPage
      industry="Chiropractic"
      industryLower="chiropractic"
      slug="chiropractic-marketing-colorado"
      subheadline="We help Colorado chiropractors fill the schedule with new patients — without paying ZocDoc or Healthgrades for shared leads."
      painPoints={[
        "New-patient flow stalls between referral spurts",
        "Big chains and franchise clinics outrank you in the Map Pack",
        "Auto-accident and PI cases get sent to other clinics because the website doesn't speak to that searcher",
      ]}
      serviceDescriptions={{
        seo: "Rank for 'chiropractor near me', auto-accident, sports injury, and pediatric searches across every Colorado market you serve.",
        googleMaps: "Dominate the local Map Pack so new-patient calls and online bookings come to your clinic first.",
        webDesign: "Fast, mobile-first chiropractic websites built for online booking and trust — not stock spine graphics.",
        ppc: "Profitable Google Ads tuned to new-patient specials, auto-accident urgency, and your clinic capacity.",
        geoAi: "Get cited when Coloradans ask ChatGPT or Gemini 'best chiropractor near me' or 'chiropractor for sciatica'.",
        socialMedia: "Local trust-building content and patient education that warms up future bookings.",
      }}
      testimonial={{
        quote:
          "New-patient bookings doubled within four months. The site finally tells the story we've been telling in person for ten years.",
        role: "Chiropractic Clinic Owner",
        city: "Colorado Springs",
      }}
      faqs={FAQS}
      cityHubLinks={[
        {
          to: "/colorado-springs",
          label: "Chiropractic marketing in Colorado Springs",
          description: "Help Springs clinics own the Map Pack and rank locally for new-patient searches.",
        },
        {
          to: "/denver-digital-marketing",
          label: "Chiropractic marketing in Denver",
          description: "Compete in Colorado's most saturated wellness market — neighborhood by neighborhood.",
        },
      ]}
    />
  ),
});
